import { apiClient } from "@/services/apiClient";
import type {
  LoginRequest,
  EnterpriseSignupRequest,
  OAuth2ExchangeRequest,
  TokenResponse,
  SessionResponse,
  UserInfoResponse,
  PasswordResetConfirmDto,
  ChangePasswordRequest,
} from "../types";

export const authService = {
  /**
   * Login with email and password (Enterprise/HR users)
   * Returns TokenResponse (tokens are also stored as HTTP-only cookies)
   */
  login: (data: LoginRequest) => apiClient.post<TokenResponse>("/auth/login", data),

  /**
   * Enterprise self-service signup. Creates a PENDING EnterpriseAccount and notifies all HR
   * staff (ENTERPRISE_REGISTERED) so it appears in the HR approval screen.
   * Endpoint: POST /enterprise/signup
   */
  register: (data: EnterpriseSignupRequest) =>
    apiClient.post<undefined>("/enterprise/signup", data),

  /**
   * Logout current session
   */
  logout: () => apiClient.post<undefined>("/auth/logout"),

  /**
   * Refresh access token
   * Note: Token is sent in body by apiClient.performRefresh
   */
  refresh: (refreshToken: string) =>
    apiClient.post<TokenResponse>("/auth/refresh", { refreshToken }),

  /**
   * Request password reset email
   * Endpoint: POST /auth/password/reset-request
   * Response: undefined (200 OK)
   */
  forgotPassword: (email: string) =>
    apiClient.post<undefined>("/auth/password/reset-request", { email }),

  /**
   * Reset password with token
   * Endpoint: POST /auth/password/reset
   * Response: undefined (200 OK)
   */
  resetPassword: (data: PasswordResetConfirmDto) =>
    apiClient.post<undefined>("/auth/password/reset", data),

  /**
   * Change password for authenticated user (General Settings)
   * Endpoint: POST /auth/password/change
   * Response: undefined (200 OK)
   */
  changePassword: (data: ChangePasswordRequest) =>
    apiClient.post<undefined>("/auth/password/change", data),

  /**
   * OAuth2 token exchange (ZenCube SSO)
   * Exchanges ZenCube authorization code for Talent Registry tokens
   *
   * Endpoint: POST /auth/oauth2/exchange
   * Backend: OAuth2TokenExchangeServiceImpl
   * Returns: TokenResponse (tokens are also set as HTTP-only cookies)
   */
  oauthExchange: (data: OAuth2ExchangeRequest) =>
    apiClient.post<TokenResponse>("/auth/oauth2/exchange", data),

  /**
   * Get current session info
   * Validates the current session and returns user details
   * Endpoint: GET /auth/session
   */
  getSession: () => apiClient.get<SessionResponse>("/auth/session"),

  /**
   * Get current user info
   * Returns detailed user information including permissions
   * Endpoint: GET /auth/me
   */
  getCurrentUser: () => apiClient.get<UserInfoResponse>("/auth/me"),

  /**
   * Verify email with token from email link
   * Endpoint: POST /auth/verify-email
   */
  verifyEmail: (token: string) =>
    apiClient.post<{ message: string }>("/auth/verify-email", { token }),

  /**
   * Resend verification email
   * Rate limited to 3 requests per hour
   * Endpoint: POST /auth/resend-verification
   */
  resendVerification: (email: string) =>
    apiClient.post<{ message: string }>("/auth/resend-verification", { email }),
};
